import { trackEvent } from "../firebase";
import { db } from "../firebase";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";

let lastPagePath = null;
const POST_LOGIN_JOURNEY_MAX_STEPS = 10;
const SESSION_LAST_PAGE_KEY = "logami_session_last_page_sent";

/**
 * Track a page view (GA4-style). All routes are tracked via this when location changes.
 */
export function trackPageView(path, title) {
  if (!path) return;
  if (lastPagePath === path) return; // dedupe (helps in React StrictMode)
  lastPagePath = path;

  trackEvent("page_view", {
    page_path: path,
    page_title: title
  });
}

/**
 * Track post-login journey for first-time and returning users: landing section and next N pages.
 * Call this from App when user is authenticated; stepIndex is 1-based (1 = first page after login).
 * Use this to see where users land after sign-in and where they go next (or drop off).
 */
export function trackPostLoginJourneyStep(path, title, stepIndex, isFirstTimeUser = false) {
  if (!path || stepIndex < 1 || stepIndex > POST_LOGIN_JOURNEY_MAX_STEPS) return;
  trackEvent("user_journey_step", {
    page_path: path,
    page_title: title || "",
    step_index: stepIndex,
    is_first_time_user: isFirstTimeUser
  });
  if (stepIndex === 1) {
    trackEvent("post_login_landing", {
      page_path: path,
      page_title: title || "",
      is_first_time_user: isFirstTimeUser
    });
  }
}

/**
 * Reset post-login step counter (e.g. on logout). Call from App when auth state becomes false.
 */
export function resetPostLoginJourney() {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem("logami_post_login_step");
  }
}

/**
 * Get and increment the post-login step for the current session. Returns 1, 2, ... up to max, then null.
 * Call when user is authenticated and you're about to track a page view.
 */
export function getNextPostLoginStep() {
  if (typeof sessionStorage === "undefined") return null;
  const key = "logami_post_login_step";
  const current = parseInt(sessionStorage.getItem(key) || "0", 10);
  const next = current + 1;
  if (next > POST_LOGIN_JOURNEY_MAX_STEPS) return null;
  sessionStorage.setItem(key, String(next));
  return next;
}

/**
 * Initialize "where they left" tracking: send session_last_page when user leaves (tab close/hide).
 * Fires once per leave using sessionStorage to avoid duplicate events on tab focus loss.
 */
export function initSessionLeaveTracking() {
  if (typeof window === "undefined" || window.__logamiSessionLeaveTrackingInstalled) return;
  window.__logamiSessionLeaveTrackingInstalled = true;

  const sendLastPage = () => {
    if (!lastPagePath) return;
    if (sessionStorage.getItem(SESSION_LAST_PAGE_KEY)) return;
    sessionStorage.setItem(SESSION_LAST_PAGE_KEY, "1");
    trackEvent("session_last_page", {
      page_path: lastPagePath
    });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendLastPage();
    else sessionStorage.removeItem(SESSION_LAST_PAGE_KEY);
  });

  window.addEventListener("pagehide", sendLastPage);
}

export function initAutoClickTracking() {
  if (typeof window === "undefined") return;
  if (window.__logamiAutoClickTrackingInstalled) return;
  window.__logamiAutoClickTrackingInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      const el = target.closest("[data-analytics-event], [data-analytics-click]");
      if (!el) return;

      const explicitEvent = el.getAttribute("data-analytics-event");
      const clickLabelAttr = el.getAttribute("data-analytics-click");

      const eventName = explicitEvent || "ui_click";
      const label =
        clickLabelAttr ||
        el.getAttribute("data-analytics-label") ||
        el.getAttribute("aria-label") ||
        (el.textContent || "").trim().slice(0, 120);

      const href =
        el instanceof HTMLAnchorElement ? el.href : el.getAttribute("href");

      trackEvent(eventName, {
        label,
        href: href || undefined
      });
    },
    true // capture: catches clicks before navigation
  );
}

// ─── Landing Page Engagement Tracking ────────────────────────────────────────

const SCROLL_MILESTONES = [25, 50, 75, 90];
const LANDING_STATS_DOC = 'analytics/landingPage';

function getTimeBucket(seconds) {
  if (seconds < 10) return 'lt_10s';
  if (seconds < 30) return '10_30s';
  if (seconds < 60) return '30_60s';
  if (seconds < 120) return '1_2min';
  if (seconds < 300) return '2_5min';
  return 'gt_5min';
}

async function writeLandingStats(updates) {
  try {
    await setDoc(doc(db, 'analytics', 'landingPage'), updates, { merge: true });
  } catch (_) {
    // Non-blocking. Firestore rules may restrict writes for unauthenticated users
  }
}

/**
 * Track how far users scroll on the landing page.
 * Fires GA4 `landing_scroll_depth` at 25 / 50 / 75 / 90% milestones (once per session).
 * Also increments Firestore counters for the admin dashboard.
 * Returns a cleanup function, call it on component unmount.
 */
export function initLandingScrollTracking({ experimentVariant = null, experimentId = null } = {}) {
  if (typeof window === 'undefined') return () => {};

  const SESSION_KEY = 'logami.landing.scroll.milestones';
  // Separate key so the session count fires on page load, regardless of scroll
  const SESSION_COUNTED_KEY = 'logami.landing.scroll.session_counted';
  const startTime = Date.now();
  const fired = new Set();

  // Restore milestones already fired earlier this session
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) JSON.parse(stored).forEach((m) => fired.add(m));
  } catch (_) {}

  // Count this session immediately, do NOT wait for the first scroll milestone.
  // Previously total_sessions only incremented on the first milestone (25%), so
  // sessions where the user bounced without scrolling were never counted, making
  // the denominator 0 and all data appear missing.
  try {
    if (!sessionStorage.getItem(SESSION_COUNTED_KEY)) {
      sessionStorage.setItem(SESSION_COUNTED_KEY, '1');
      writeLandingStats({ 'scroll.total_sessions': increment(1) });
    }
  } catch (_) {}

  const handler = () => {
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((window.scrollY / docHeight) * 100);

    for (const milestone of SCROLL_MILESTONES) {
      if (pct >= milestone && !fired.has(milestone)) {
        fired.add(milestone);

        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify([...fired])); } catch (_) {}

        const timeToReachS = Math.round((Date.now() - startTime) / 1000);

        trackEvent('landing_scroll_depth', {
          depth_pct: milestone,
          time_to_reach_s: timeToReachS,
          experiment_variant: experimentVariant,
          experiment_id: experimentId,
        });

        writeLandingStats({ [`scroll.reached_${milestone}`]: increment(1) });
      }
    }
  };

  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
}

/**
 * Track how long first-time visitors spend on the landing page.
 * Fires GA4 `landing_time_spent` once per session (on tab hide / page leave / unmount).
 * Also increments Firestore time bucket counters for the admin dashboard.
 * Returns a cleanup function, call it on component unmount.
 */
export function initLandingTimeTracking({ experimentVariant = null, experimentId = null } = {}) {
  if (typeof window === 'undefined') return () => {};

  const SESSION_KEY = 'logami.landing.time.tracked';
  try {
    if (sessionStorage.getItem(SESSION_KEY) === '1') return () => {};
  } catch (_) {}

  const startTime = Date.now();
  let fired = false;

  const send = () => {
    if (fired) return;
    fired = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) {}

    const seconds = Math.round((Date.now() - startTime) / 1000);
    const bucket = getTimeBucket(seconds);

    trackEvent('landing_time_spent', {
      seconds,
      time_bucket: bucket,
      experiment_variant: experimentVariant,
      experiment_id: experimentId,
    });

    writeLandingStats({
      [`time.${bucket}`]: increment(1),
      'time.total': increment(1),
      lastUpdated: new Date().toISOString(),
    });
  };

  const onVisibility = () => { if (document.visibilityState === 'hidden') send(); };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', send);

  return () => {
    send(); // Fire on React navigation (component unmount)
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', send);
  };
}

/**
 * Fetch landing page engagement stats from Firestore (for admin dashboard).
 * Transforms flat dot-notation keys ("scroll.total_sessions") into nested
 * objects ({ scroll: { total_sessions: ... } }) as expected by AdminDashboard.
 */
export async function getLandingPageStats() {
  try {
    const snap = await getDoc(doc(db, 'analytics', 'landingPage'));
    if (!snap.exists()) return null;
    const raw = snap.data();
    const result = {};
    for (const [key, value] of Object.entries(raw)) {
      const dotIndex = key.indexOf('.');
      if (dotIndex !== -1) {
        const group = key.slice(0, dotIndex);
        const subKey = key.slice(dotIndex + 1);
        if (!result[group]) result[group] = {};
        result[group][subKey] = value;
      } else {
        result[key] = value;
      }
    }
    return result;
  } catch (_) {
    return null;
  }
}

